import apiClient from '../utils/apiClient';
import { ApiResponse, ApiError } from '../types/api';
import { AxiosRequestConfig, AxiosError } from 'axios';

/**
 * Base API Service class with common HTTP methods
 */
export class ApiService {
  /**
   * Generic GET request
   */
  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await apiClient.get<T | ApiResponse<T>>(url, config);
      return this.extractData<T>(response.data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generic POST request
   */
  protected async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await apiClient.post<T | ApiResponse<T>>(url, data, config);
      return this.extractData<T>(response.data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generic PUT request
   */
  protected async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await apiClient.put<ApiResponse<T>>(url, data, config);
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generic PATCH request
   */
  protected async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await apiClient.patch<ApiResponse<T>>(url, data, config);
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generic DELETE request
   */
  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await apiClient.delete<ApiResponse<T>>(url, config);
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Extract data from various response structures
   */
  private extractData<T>(responseData: T | ApiResponse<T>): T {
    // Handle direct array response
    if (Array.isArray(responseData)) {
      return responseData;
    }

    // Handle object response with nested data
    if (responseData && typeof responseData === 'object' && 'data' in responseData) {
      const apiResponse = responseData as ApiResponse<T>;
      // Return full response for paginated data
      if ('metaData' in apiResponse || 'pagination' in apiResponse) {
        return responseData as T;
      }
      return apiResponse.data;
    }

    return responseData;
  }

  /**
   * Handle and normalize API errors
   */
  private handleError(error: unknown): ApiError {
    // Return if already an ApiError
    if (this.isApiError(error)) {
      return error;
    }

    // Handle Axios errors
    if (this.isAxiosError(error)) {
      const errorData = error.response?.data as Record<string, unknown> | undefined;
      
      if (error.response) {
        return {
          message: this.extractErrorMessage(errorData) || 'An error occurred',
          status: error.response.status,
          code: this.extractErrorCode(errorData),
          errors: errorData?.errors as Record<string, string[]> | undefined,
        };
      }

      if (error.request) {
        return {
          message: 'Network error. Please check your connection.',
          status: 0,
        };
      }
    }

    // Handle generic errors
    if (error instanceof Error) {
      return {
        message: error.message || 'An unexpected error occurred',
      };
    }

    return {
      message: 'An unexpected error occurred',
    };
  }

  private isApiError(error: unknown): error is ApiError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      typeof (error as ApiError).message === 'string'
    );
  }

  private isAxiosError(error: unknown): error is AxiosError {
    return (error as AxiosError)?.isAxiosError === true;
  }

  private extractErrorMessage(data?: Record<string, unknown>): string | undefined {
    if (!data) return undefined;
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;
    return undefined;
  }

  private extractErrorCode(data?: Record<string, unknown>): string | undefined {
    if (!data) return undefined;
    if (typeof data.code === 'string') return data.code;
    if (typeof data.error === 'string') return data.error;
    return undefined;
  }
}
